import { useProjects } from "@/api/project";
import { useUserInfo } from "@/api/user";
import DoubleChevronLeftIcon from "@/assets/icons/chevron_double_left.svg?react";
import DoubleChevronRightIcon from "@/assets/icons/chevron_double_right.svg?react";
import GmtLogo from "@/assets/icons/gmt_logo.svg?react";
import GmtLogoAndName from "@/assets/icons/gmt_logo_and_name.svg?react";
import HelpIcon from "@/assets/icons/help_outlined.svg?react";
import LibraryIcon from "@/assets/icons/library.svg?react";
import ProjectIcon from "@/assets/icons/project.svg?react";
import SettingsIcon from "@/assets/icons/settings.svg?react";
import { API_URL } from "@/config";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { appActions, appSelectors } from "@/redux/features/appSlice";
import { blue } from "@/utils/Colors";
import { Box, LinearProgress } from "@mui/material";
import Divider from "@mui/material/Divider";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import { CSSObject, styled, Theme } from "@mui/material/styles";
import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AcceptCGUModal from "../Account/AcceptCGUModal";
import NavItemLvlOne from "./NavItemLvlOne";
import NavItemLvlTwo from "./NavItemLvlTwo";
import SideBarProjectContent from "./SideBarProjectContent";

export const drawerWidth = 245;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(8)} + 1px)`,
  // [theme.breakpoints.up("sm")]: {
  //   width: `calc(${theme.spacing(8)} + 1px)`,
  // },
});

export const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

function SideBar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const sideDrawerIsOpen = useAppSelector(appSelectors.getSideDrawerIsOpen);
  const dispatch = useAppDispatch();

  const toggleDrawer = () => {
    dispatch(appActions.setSideDrawerIsOpen(!sideDrawerIsOpen));
  };

  const [openSections, setOpenSections] = useState<Set<string>>(new Set());

  function handleClick(sectionId: string) {
    setOpenSections((prev) => {
      if (!prev.has(sectionId)) {
        const newSelection = new Set(prev);
        newSelection.add(sectionId);
        return newSelection;
      }
      const newSelection = new Set(prev);
      newSelection.delete(sectionId);
      return newSelection;
    });
  }

  const projectsQuery = useProjects({});

  const userToken = useAppSelector((state) => state.login.userToken);
  //We are sure that there is a token because if not the AuthGuard would have redirected us
  const jwt = jwtDecode(userToken!.accessToken);
  const userInfoQuery = useUserInfo({
    userId: jwt?.sub as string,
    config: { enabled: jwt?.sub != null },
  });

  useEffect(() => {
    if (userInfoQuery.isSuccess) {
      if (i18n.language !== userInfoQuery.data.language)
        i18n.changeLanguage(userInfoQuery.data.language);
    }
  }, [userInfoQuery.isSuccess]);

  return (
    <Drawer
      variant="permanent"
      open={sideDrawerIsOpen}
      PaperProps={{
        sx: { backgroundColor: blue[100], display: "flex", flexFlow: "column" },
      }}
    >
      {/* Top bar */}
      <DrawerHeader
        sx={{
          height: 6,
          padding: 1.5,
          gap: 1.5,
          borderBottom: 0.0625,
          borderBottomColor: blue[300],
          ...(API_URL.includes("devapi") && { backgroundColor: "red" }),
        }}
      >
        <IconButton
          onClick={() => navigate("/profile")}
          sx={{
            height: 3,
            minWidth: 0,
            mr: sideDrawerIsOpen ? 3 : "auto",
            justifyContent: "center",
          }}
        >
          {sideDrawerIsOpen ? <GmtLogoAndName /> : <GmtLogo />}
        </IconButton>
      </DrawerHeader>
      <Divider />
      {/* Menu */}
      <Box
        sx={{
          paddingX: 2,
          paddingTop: 2,
          paddingBottom: 4,
          justifyContent: "space-between",
          flex: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Top */}
        <Box sx={{ gap: 3 }}>
          {/* Chevron */}
          <List>
            <ListItem disablePadding sx={{ display: "flex", gap: 1 }}>
              <ListItemButton
                sx={{
                  justifyContent: sideDrawerIsOpen ? "center" : "center",
                }}
                onClick={toggleDrawer}
              >
                <ListItemIcon
                  sx={{
                    justifyContent: "center",
                  }}
                >
                  {sideDrawerIsOpen ? (
                    <DoubleChevronLeftIcon />
                  ) : (
                    <DoubleChevronRightIcon />
                  )}
                </ListItemIcon>
              </ListItemButton>
            </ListItem>
          </List>
          {/* Menu */}
          <List>
            {/* Documents */}
            <NavItemLvlOne
              icon={<LibraryIcon />}
              label={t("myDocuments")}
              sideDrawerIsOpen={sideDrawerIsOpen}
              pathsToHighlight={["/documents"]}
              openSections={openSections}
              onClickButton={() => navigate("/documents")}
            />
            {/* Projects */}
            <NavItemLvlOne
              icon={<ProjectIcon />}
              label={t("projects")}
              sideDrawerIsOpen={sideDrawerIsOpen}
              pathsToHighlight={["/project", "/profile"]}
              openSections={openSections}
              handleClickArrow={handleClick}
              onClickButton={() => navigate("/profile")}
            >
              {(projectsQuery.isPending || projectsQuery.isError) && (
                <LinearProgress />
              )}
              {projectsQuery.isSuccess &&
                projectsQuery.data.map((project) => (
                  <NavItemLvlTwo
                    key={project.id}
                    project={project}
                    pathsToHighlight={[]}
                    openSections={openSections}
                    handleClickArrow={handleClick}
                  >
                    <SideBarProjectContent
                      projectId={project.id}
                      openSections={openSections}
                    />
                  </NavItemLvlTwo>
                ))}
            </NavItemLvlOne>
          </List>
        </Box>
        {/* Down */}
        <Box>
          <List
            sx={{
              gap: 0.5,
            }}
          >
            <NavItemLvlOne
              icon={<SettingsIcon />}
              label={t("settings")}
              sideDrawerIsOpen={sideDrawerIsOpen}
              pathsToHighlight={["/account"]}
              onClickButton={() => navigate("/account")}
              openSections={openSections}
            />
            {/* Help */}
            <NavItemLvlOne
              icon={<HelpIcon />}
              label={t("help")}
              sideDrawerIsOpen={sideDrawerIsOpen}
              pathsToHighlight={["/help"]}
              openSections={openSections}
              onClickButton={() => {
                window.location.href = "mailto:contact@graphmytech.com";
              }}
            />
          </List>
        </Box>
      </Box>
      <AcceptCGUModal
        open={
          userInfoQuery.data !== undefined &&
          userInfoQuery.data.acceptedCgu === false
        }
        userId={jwt?.sub as string}
      />
    </Drawer>
  );
}

export default SideBar;
